// src/components/Backtest.tsx
import { useState } from 'react';
import ExpressionEditor from './ExpressionEditor/ExpressionEditor';
import { callRefresh } from '../api/refresh';
import { useAppStore } from '../state/store';
import { expandExpressionGraph } from '../utils/expandExpressionGraph';
import type { PositionSizing, SimpleBacktestRequest } from '../state/types';

type ExpressionTab = 'entry' | 'exit';

export function Backtest() {
  const [expressionTab, setExpressionTab] = useState<ExpressionTab>('entry');
  const backtestSettings = useAppStore((s) => s.backtestSettings);
  const setBacktestSettings = useAppStore((s) => s.setBacktestSettings);
  const instruments = useAppStore((s) => s.instruments);
  const primaryInstrumentId = useAppStore((s) => s.primaryInstrumentId);
  const indicators = useAppStore((s) => s.indicators);
  const rootId = useAppStore((s) => s.expressionGraph.rootId);
  const expressions = useAppStore((s) => s.expressionGraph.expressions);
  const exitRootId = useAppStore((s) => s.exitExpressionGraph.rootId);
  const exitExpressions = useAppStore((s) => s.exitExpressionGraph.expressions);
  const constants = useAppStore((s) => s.constants);
  const backtestLoading = useAppStore((s) => s.backtestLoading);
  const setBacktestLoading = useAppStore((s) => s.setBacktestLoading);
  const selectedTradeSetKey = useAppStore((s) => s.selectedTradeSetKey);

  const {
    tradeDirection,
    backtestType,
    entryType,
    exitType,
    positionSizing,
    fixedShares,
    fixedAmountDollars,
    fixedFractionPct,
    initialCapital,
    allowFractionalShares,
    minLotSize,
  } = backtestSettings;

  const expressionGraph = { rootId, expressions };
  const exitExpressionGraph = { rootId: exitRootId, expressions: exitExpressions };

  const handleRunBacktest = async () => {
    setBacktestLoading(true);
    try {
      const expandedGraph = expandExpressionGraph(expressionGraph, constants);

      console.log('[FRONTEND] Clean UI graph:', expressionGraph);
      console.log('[FRONTEND] Expanded graph sent to backend:', expandedGraph);

      const backtestRequest: SimpleBacktestRequest = {
        expressionGraph: expandedGraph,
        num_bars: 5,
        trade_direction: tradeDirection,
        num_units: 1,
        backtest_type: backtestType,
        entry_type: entryType,
        exit_type: exitType,
        position_sizing: positionSizing,
        fixed_shares: positionSizing === 'fixed_shares' ? fixedShares : undefined,
        fixed_amount_dollars: positionSizing === 'fixed_amount' ? fixedAmountDollars : undefined,
        fixed_fraction_pct: positionSizing === 'fixed_fraction' ? fixedFractionPct : undefined,
        initial_capital: positionSizing === 'fixed_fraction' ? initialCapital : undefined,
        allow_fractional_shares: allowFractionalShares,
        min_lot_size: minLotSize !== '' && !allowFractionalShares ? Number(minLotSize) : undefined,
        simpleTradeSet: selectedTradeSetKey,
        ...(backtestType === 'exit_expression'
          ? { exitExpressionGraph: expandExpressionGraph(exitExpressionGraph, constants) }
          : {}),
      };

      const request = {
        refresh: "backtest" as const,
        instruments,
        primaryInstrumentId: primaryInstrumentId ?? '',
        indicators,
        backtestRequest,
      };

      console.log('[FINAL PAYLOAD SENT TO /refresh]', JSON.stringify(request, null, 2));

      const response = await callRefresh(request);
      useAppStore.getState().loadFromRefreshResponse(response);
      useAppStore.getState().setLastBacktestRequest(backtestRequest);
      useAppStore.getState().setBacktestParamsDirtySinceRun(false);
    } catch (err) {
      console.error("Failed to run backtest:", err);
    } finally {
      setBacktestLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-ink">Exit Type:</span>
        <select
          value={backtestType}
          onChange={(e) => setBacktestSettings({ backtestType: e.target.value as 'simple' | 'expression_hold' | 'exit_expression' })}
          className="px-4 py-2 text-sm border border-rule rounded-sm bg-newsprint-dark text-ink focus:outline-none focus:border-ink"
          aria-label="Exit type"
        >
          <option value="simple">Simple</option>
          <option value="expression_hold">Expression Hold</option>
          <option value="exit_expression">Exit Expression</option>
        </select>
        <span className="text-sm font-medium text-ink">Entry:</span>
        <select
          value={entryType}
          onChange={(e) => setBacktestSettings({ entryType: e.target.value as 'open' | 'close' | 'next_bar_open' })}
          className="px-4 py-2 text-sm border border-rule rounded-sm bg-newsprint-dark text-ink focus:outline-none focus:border-ink"
          aria-label="Entry price timing"
        >
          <option value="close">At Close</option>
          <option value="open">At Open</option>
          <option value="next_bar_open">At Next Bar Open</option>
        </select>
        <span className="text-sm font-medium text-ink">Exit:</span>
        <select
          value={exitType}
          onChange={(e) => setBacktestSettings({ exitType: e.target.value as 'open' | 'close' })}
          className="px-4 py-2 text-sm border border-rule rounded-sm bg-newsprint-dark text-ink focus:outline-none focus:border-ink"
          aria-label="Exit price timing"
        >
          <option value="close">At Close</option>
          <option value="open">At Open</option>
        </select>
        <span className="text-sm font-medium text-ink">Direction:</span>
        <div
          role="radiogroup"
          aria-label="Trade direction"
          className="inline-flex rounded-sm border border-rule bg-newsprint-dark p-0.5"
        >
          {(['long', 'short'] as const).map((dir) => (
            <button
              key={dir}
              type="button"
              role="radio"
              aria-checked={tradeDirection === dir}
              onClick={() => setBacktestSettings({ tradeDirection: dir })}
              className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                tradeDirection === dir
                  ? 'bg-ink text-newsprint'
                  : 'text-ink hover:bg-newsprint'
              }`}
            >
              {dir === 'long' ? 'Long' : 'Short'}
            </button>
          ))}
        </div>
      </div>
      <div className="border border-rule rounded-sm overflow-hidden bg-newsprint-panel">
        <div className="flex border-b border-rule bg-newsprint-dark" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={expressionTab === 'entry'}
            aria-controls="expression-tab-entry"
            id="tab-entry"
            onClick={() => setExpressionTab('entry')}
            className={`px-4 py-2 text-sm font-medium ${
              expressionTab === 'entry' ? 'bg-newsprint-panel text-ink border-b-2 border-transparent -mb-px border-t-2 border-t-ink' : 'text-ink/80 hover:text-ink'
            }`}
          >
            Entry expression
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={expressionTab === 'exit'}
            aria-controls="expression-tab-exit"
            id="tab-exit"
            disabled={backtestType !== 'exit_expression'}
            onClick={() => backtestType === 'exit_expression' && setExpressionTab('exit')}
            className={`px-4 py-2 text-sm font-medium ${
              expressionTab === 'exit' ? 'bg-newsprint-panel text-ink border-b-2 border-transparent -mb-px border-t-2 border-t-ink' : 'text-ink/80 hover:text-ink'
            } ${backtestType !== 'exit_expression' ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            Exit expression
          </button>
        </div>
        <div
          id="expression-tab-entry"
          role="tabpanel"
          aria-labelledby="tab-entry"
          hidden={expressionTab !== 'entry'}
          className="p-0"
        >
          {expressionTab === 'entry' && <ExpressionEditor graphKey="entry" />}
        </div>
        <div
          id="expression-tab-exit"
          role="tabpanel"
          aria-labelledby="tab-exit"
          hidden={expressionTab !== 'exit'}
          className="p-0"
        >
          {expressionTab === 'exit' ? (
            backtestType === 'exit_expression' ? (
              <ExpressionEditor graphKey="exit" />
            ) : (
              <p className="p-4 text-sm text-ink/80">Select &quot;Exit Expression&quot; as Exit Type to edit the exit expression.</p>
            )
          ) : null}
        </div>
      </div>
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-ink font-headline">Position sizing</h3>
        <div className="flex flex-wrap items-center gap-3">
          <label htmlFor="positionSizing" className="text-sm font-medium text-ink">
            Position sizing:
          </label>
          <select
            id="positionSizing"
            value={positionSizing}
            onChange={(e) => setBacktestSettings({ positionSizing: e.target.value as PositionSizing })}
            className="px-4 py-2 text-sm border border-rule rounded-sm bg-newsprint-panel text-ink focus:outline-none focus:border-ink"
            aria-label="Position sizing"
          >
            <option value="fixed_shares">Fixed shares</option>
            <option value="fixed_amount">Fixed amount</option>
            <option value="fixed_fraction">Fixed fraction</option>
          </select>

          {positionSizing === 'fixed_shares' && (
            <>
              <label htmlFor="fixedShares" className="text-sm font-medium text-ink">
                Shares per trade:
              </label>
              <input
                id="fixedShares"
                type="number"
                min={1}
                value={fixedShares}
                onChange={(e) => setBacktestSettings({ fixedShares: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                className="w-24 px-2 py-1.5 text-sm border-2 border-rule rounded-sm bg-newsprint-panel text-ink focus:outline-none focus:border-ink focus:ring-4 focus:ring-newsprint-dark"
                aria-label="Shares per trade"
              />
            </>
          )}

          {positionSizing === 'fixed_amount' && (
            <>
              <label htmlFor="fixedAmount" className="text-sm font-medium text-ink">
                Capital per trade ($):
              </label>
              <input
                id="fixedAmount"
                type="number"
                min={0}
                step={1}
                value={fixedAmountDollars}
                onChange={(e) => setBacktestSettings({ fixedAmountDollars: Math.max(0, parseFloat(e.target.value) || 0) })}
                className="w-28 px-2 py-1.5 text-sm border-2 border-rule rounded-sm bg-newsprint-panel text-ink focus:outline-none focus:border-ink focus:ring-4 focus:ring-newsprint-dark"
                aria-label="Capital per trade in dollars"
              />
              <span className="text-sm font-medium text-ink">Shares:</span>
              <div
                role="radiogroup"
                aria-label="Shares: Fractional or whole"
                className="inline-flex rounded-sm border border-rule bg-newsprint-dark p-0.5"
              >
                {(['Fractional', 'Whole'] as const).map((option) => {
                  const isFractional = option === 'Fractional';
                  const isSelected = allowFractionalShares === isFractional;
                  return (
                    <button
                      key={option}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      onClick={() => {
                        setBacktestSettings({
                          allowFractionalShares: isFractional,
                          ...(isFractional ? { minLotSize: '' as const } : {}),
                        });
                      }}
                      className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                        isSelected ? 'bg-ink text-newsprint' : 'text-ink hover:bg-newsprint'
                      }`}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
              {!allowFractionalShares && (
                <>
                  <label htmlFor="minLotSize" className="text-sm font-medium text-ink">
                    Min lot size:
                  </label>
                  <input
                    id="minLotSize"
                    type="number"
                    min={1}
                    value={minLotSize}
                    onChange={(e) => {
                      const v = e.target.value;
                      setBacktestSettings({ minLotSize: v === '' ? '' : parseInt(v, 10) || 0 });
                    }}
                    placeholder="Optional"
                    className="w-20 px-2 py-1.5 text-sm border-2 border-rule rounded-sm bg-newsprint-panel text-ink focus:outline-none focus:border-ink focus:ring-4 focus:ring-newsprint-dark"
                    aria-label="Minimum lot size (optional)"
                  />
                </>
              )}
            </>
          )}

          {positionSizing === 'fixed_fraction' && (
            <>
              <label htmlFor="initialCapital" className="text-sm font-medium text-ink">
                Initial capital ($):
              </label>
              <input
                id="initialCapital"
                type="number"
                min={0}
                step={1}
                value={initialCapital}
                onChange={(e) => setBacktestSettings({ initialCapital: Math.max(0, parseFloat(e.target.value) || 0) })}
                className="w-28 px-2 py-1.5 text-sm border-2 border-rule rounded-sm bg-newsprint-panel text-ink focus:outline-none focus:border-ink focus:ring-4 focus:ring-newsprint-dark"
                aria-label="Initial capital in dollars"
              />
              <label htmlFor="fixedFractionPct" className="text-sm font-medium text-ink">
                % of capital per trade:
              </label>
              <input
                id="fixedFractionPct"
                type="number"
                min={0}
                max={100}
                step={0.5}
                value={fixedFractionPct}
                onChange={(e) => setBacktestSettings({ fixedFractionPct: Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)) })}
                className="w-20 px-2 py-1.5 text-sm border-2 border-rule rounded-sm bg-newsprint-panel text-ink focus:outline-none focus:border-ink focus:ring-4 focus:ring-newsprint-dark"
                aria-label="Percentage of capital per trade"
              />
              <span className="text-sm font-medium text-ink">Shares:</span>
              <div
                role="radiogroup"
                aria-label="Shares: Fractional or whole"
                className="inline-flex rounded-sm border border-rule bg-newsprint-dark p-0.5"
              >
                {(['Fractional', 'Whole'] as const).map((option) => {
                  const isFractional = option === 'Fractional';
                  const isSelected = allowFractionalShares === isFractional;
                  return (
                    <button
                      key={option}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      onClick={() => {
                        setBacktestSettings({
                          allowFractionalShares: isFractional,
                          ...(isFractional ? { minLotSize: '' as const } : {}),
                        });
                      }}
                      className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                        isSelected ? 'bg-ink text-newsprint' : 'text-ink hover:bg-newsprint'
                      }`}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
              {!allowFractionalShares && (
                <>
                  <label htmlFor="minLotSizeFrac" className="text-sm font-medium text-ink">
                    Min lot size:
                  </label>
                  <input
                    id="minLotSizeFrac"
                    type="number"
                    min={1}
                    value={minLotSize}
                    onChange={(e) => setBacktestSettings({ minLotSize: e.target.value === '' ? '' : parseInt(e.target.value, 10) || 0 })}
                    placeholder="Optional"
                    className="w-20 px-2 py-1.5 text-sm border-2 border-rule rounded-sm bg-newsprint-panel text-ink focus:outline-none focus:border-ink focus:ring-4 focus:ring-newsprint-dark"
                    aria-label="Minimum lot size (optional)"
                  />
                </>
              )}
            </>
          )}
        <button
          type="button"
          onClick={handleRunBacktest}
          disabled={backtestLoading}
          className="bg-ink text-newsprint px-4 py-2 rounded-sm hover:opacity-90 disabled:opacity-70 disabled:cursor-not-allowed inline-flex items-center gap-2"
        >
          {backtestLoading && (
            <span className="inline-block h-4 w-4 border-2 border-newsprint border-t-transparent rounded-full animate-spin" aria-hidden />
          )}
          {backtestLoading ? 'Running…' : 'Run Backtest'}
        </button>
        </div>
      </div>
    </div>
  );
}

export default Backtest;